/**
 * StudioPanelRegistry - Phase A Mission A1
 *
 * Purely declarative registry describing the panels that can occupy
 * the three main slots in Studio v2:
 * - center-main: The main content area
 * - right-rail: The right panel (Soma Noesis)
 * - drawer: The bottom drawer tabs
 *
 * This registry provides metadata only - no behavioral changes.
 * Components are imported lazily in studio-v2.tsx.
 * PanelManagerBee integration comes in A2.
 */

import type { ReactNode } from 'react';

/**
 * The three slots where panels can be rendered in Studio v2
 */
export type StudioPanelSlot = 'center-main' | 'right-rail' | 'drawer';

/**
 * Descriptor for a panel that can be rendered in Studio v2
 */
export interface StudioPanelDescriptor {
    /** Unique identifier for this panel */
    id: string;
    /** Which slot this panel occupies */
    slot: StudioPanelSlot;
    /** Human-readable label for the panel */
    label: string;
    /** Optional icon for sidebar/dock display */
    icon?: ReactNode;
    /** Optional app key - maps to SidebarV2 Apps section */
    appKey?: string;
    /** Optional route pattern this panel responds to */
    routePattern?: string;
    /** Whether this is the default panel for its slot */
    isDefault?: boolean;
    /** Optional description for tooltips/help */
    description?: string;
    /**
     * D1.1: Primary flag for Dock 2.0 clustering
     * - true: Always visible in sidebar (primary apps)
     * - false/undefined: Collapsed into "More" group (secondary apps)
     */
    primary?: boolean;
}

/**
 * Registry of all panels available in Studio v2
 *
 * Note: Components are imported lazily in studio-v2.tsx to avoid
 * circular dependencies. This registry stores metadata only.
 */
export const studioPanelRegistry: StudioPanelDescriptor[] = [
    // ============================================
    // CENTER-MAIN PANELS
    // ============================================

    // Default view - Missions/ABDA Browser host
    {
        id: 'missions-main',
        slot: 'center-main',
        label: 'Missions',
        appKey: 'missions',
        routePattern: '/hivecore/studio-v2',
        isDefault: true,
        primary: true,
        description: 'Mission control and ABDA browser',
    },

    // ABDA Live View - Live browser session streaming
    {
        id: 'abda-live-view-main',
        slot: 'center-main',
        label: 'Browser Live View',
        appKey: 'browser-live-view',
        routePattern: '/hivecore/studio-v2/app/browser-live-view',
        primary: true,
        description: 'Live screenshot stream of headless browser sessions with evidence capture',
    },

    // Mission Detail View
    {
        id: 'mission-detail',
        slot: 'center-main',
        label: 'Mission Detail',
        routePattern: '/hivecore/studio-v2/mission/:missionId',
        description: 'Detailed view of a specific mission',
    },

    // Mission View (Builder missions telemetry)
    {
        id: 'mission-view',
        slot: 'center-main',
        label: 'Mission View',
        routePattern: '/hivecore/studio-v2/missions/:builderMissionId',
        description: 'Mission telemetry and status view',
    },

    // Run View (live execution)
    {
        id: 'run-view',
        slot: 'center-main',
        label: 'Run View',
        routePattern: '/hivecore/studio-v2/run/:runId',
        description: 'Live run execution view',
    },

    // Workspace Explorer
    {
        id: 'workspace-main',
        slot: 'center-main',
        label: 'Workspace Explorer',
        routePattern: '/hivecore/studio-v2/workspace/:workspaceId',
        description: 'File and workspace browser',
    },

    // Matrix Panel
    {
        id: 'matrix-main',
        slot: 'center-main',
        label: 'Matrix',
        appKey: 'matrix',
        routePattern: '/hivecore/studio-v2/matrix',
        description: 'Global telemetry matrix inspector',
    },

    // Builder Panel
    {
        id: 'builder-main',
        slot: 'center-main',
        label: 'Builder',
        appKey: 'builder',
        routePattern: '/hivecore/studio-v2/app/builder',
        primary: true,
        description: 'Mission builder and orchestration',
    },

    // ConversionOps Console
    {
        id: 'convops-main',
        slot: 'center-main',
        label: 'ConvOps',
        appKey: 'convops',
        routePattern: '/hivecore/studio-v2/app/convops',
        primary: true,
        description: 'Data conversion operations console',
    },

    // Deployment Panel
    {
        id: 'deployment-main',
        slot: 'center-main',
        label: 'Deployment',
        appKey: 'deployment',
        routePattern: '/hivecore/studio-v2/app/deployment',
        description: 'Deployment orchestration and status',
    },

    // Delivery Cockpit (Release Index + Object Store)
    {
        id: 'delivery-cockpit-main',
        slot: 'center-main',
        label: 'Delivery Cockpit',
        appKey: 'delivery-cockpit',
        routePattern: '/hivecore/studio-v2/app/delivery-cockpit',
        primary: true,
        description: 'Release Index-backed delivery cockpit (no GitHub/S3 listing)',
    },

    // Meta Telemetry Panel (Governance Weighting)
    {
        id: 'meta-main',
        slot: 'center-main',
        label: 'Meta Telemetry',
        appKey: 'meta',
        routePattern: '/hivecore/studio-v2/app/meta',
        description: 'OS health metrics and Bee governance weights',
    },

    // Agent Manager Panel (Phase 4E)
    {
        id: 'agent-manager-main',
        slot: 'center-main',
        label: 'Agent Manager',
        appKey: 'agent-manager',
        routePattern: '/hivecore/studio-v2/app/agent-manager',
        primary: true,
        description: 'R10/R11 spec-autonomy governance and mission visibility',
    },

    // Capability Governance Panel (Phase 2 Session 5)
    {
        id: 'capability-governance-main',
        slot: 'center-main',
        label: 'Capability Governance',
        appKey: 'capability-governance',
        routePattern: '/hivecore/studio-v2/app/capability-governance',
        primary: true,
        description: 'Capability telemetry, usage tracking, and governance violations',
    },
    {
        id: 'daily-narrative-main',
        slot: 'center-main',
        label: 'Daily Narrative',
        appKey: 'daily-narrative',
        routePattern: '/hivecore/studio-v2/app/daily-narrative',
        primary: true,
        description: 'Daily operations narrative with PEV stats and WORM verification',
    },

    // Bee Identity Panel (Phase 2 Session 6)
    {
        id: 'bee-identity-main',
        slot: 'center-main',
        label: 'Bee Identity',
        appKey: 'bee-identity',
        routePattern: '/hivecore/studio-v2/app/bee-identity',
        primary: true,
        description: 'Structural hashes and integrity fingerprints for Bees',
    },

    // EvoSignals Panel (Phase 2 Session 8)
    {
        id: 'evo-signals-main',
        slot: 'center-main',
        label: 'Evolution Inbox',
        appKey: 'evo-signals',
        routePattern: '/hivecore/studio-v2/app/evo-signals',
        primary: true,
        description: 'EvoSignal collection from sandbox, governance, and runtime violations',
    },

    // EvoProblems Panel (Phase 2 Session 9)
    {
        id: 'evo-problems-main',
        slot: 'center-main',
        label: 'Evolution Problems',
        appKey: 'evo-problems',
        routePattern: '/hivecore/studio-v2/app/evo-problems',
        primary: true,
        description: 'Classified EvoProblems grouped by type, severity, and Bee',
    },

    // EvoSpecs Panel (Phase 2 Session 10)
    {
        id: 'evo-specs-main',
        slot: 'center-main',
        label: 'Evolution Specs',
        appKey: 'evo-specs',
        routePattern: '/hivecore/studio-v2/app/evo-specs',
        primary: true,
        description: 'EvoSpec drafts generated from EvoProblems for MetaBuild',
    },

    // EvoRunner Panel (Phase 3 Session 1)
    {
        id: 'evo-runner-main',
        slot: 'center-main',
        label: 'Evolution Runner',
        appKey: 'evo-runner',
        routePattern: '/hivecore/studio-v2/app/evo-runner',
        primary: true,
        description: 'Run queued EvoSpecs through MetaBuild to generate PRs',
    },

    // EvoProposals Panel (Stage C1)
    {
        id: 'evo-proposals-main',
        slot: 'center-main',
        label: 'Evolution Proposals',
        appKey: 'evo-proposals',
        routePattern: '/hivecore/studio-v2/app/evo-proposals',
        primary: true,
        description: 'Structured change proposals generated from EvoSignals',
    },

    // PatchPlans Panel (Stage C2)
    {
        id: 'patch-plans-main',
        slot: 'center-main',
        label: 'Patch Plans',
        appKey: 'patch-plans',
        routePattern: '/hivecore/studio-v2/app/patch-plans',
        primary: true,
        description: 'File-level change instructions derived from EvoProposals',
    },

    // ExecutionMonitor Panel (Stage C4)
    {
        id: 'execution-monitor-main',
        slot: 'center-main',
        label: 'Execution Monitor',
        appKey: 'execution-monitor',
        routePattern: '/hivecore/studio-v2/app/execution-monitor',
        primary: true,
        description: 'Real-time PatchPlan execution progress and metrics',
    },

    // GovernedPR Panel (Stage C5)
    {
        id: 'governed-pr-main',
        slot: 'center-main',
        label: 'Governed PRs',
        appKey: 'governed-prs',
        routePattern: '/hivecore/studio-v2/app/governed-prs',
        primary: true,
        description: 'PRs with full governance metadata, lineage, and safety state',
    },

    // MissionTimeline Panel (Studio v3.0 Foundation)
    {
        id: 'mission-timeline-main',
        slot: 'center-main',
        label: 'Mission Timeline',
        appKey: 'mission-timeline',
        routePattern: '/hivecore/studio-v2/app/mission-timeline',
        primary: true,
        description: 'View all system activity organized by mission with event filtering',
    },

    // Capabilities Studio Panel (Phase 4J)
    {
        id: 'capabilities-studio-main',
        slot: 'center-main',
        label: 'Capabilities Studio',
        appKey: 'capabilities-studio',
        routePattern: '/hivecore/studio-v2/app/capabilities-studio',
        primary: true,
        description: 'Skill authoring, capacity management, and execution governance',
    },

    // App panels (SidebarV2 apps)
    {
        id: 'codeops-main',
        slot: 'center-main',
        label: 'CodeOps',
        appKey: 'codeops',
        routePattern: '/hivecore/studio-v2/app/codeops',
        description: 'Code operations and automation',
    },
    {
        id: 'buildlab-main',
        slot: 'center-main',
        label: 'BuildLab',
        appKey: 'buildlab',
        routePattern: '/hivecore/studio-v2/app/buildlab',
        description: 'Build and deployment lab',
    },
    {
        id: 'echogrid-main',
        slot: 'center-main',
        label: 'EchoGrid',
        appKey: 'echogrid',
        routePattern: '/hivecore/studio-v2/app/echogrid',
        description: 'Real-time telemetry grid',
    },
    {
        id: 'syntara-main',
        slot: 'center-main',
        label: 'Syntara',
        appKey: 'syntara',
        routePattern: '/hivecore/studio-v2/app/syntara',
        description: 'Syntara voice interface',
    },
    {
        id: 'canvas-main',
        slot: 'center-main',
        label: 'Canvas',
        appKey: 'canvas',
        routePattern: '/hivecore/studio-v2/app/canvas',
        description: 'Visual canvas workspace',
    },

    // Meta Telemetry Panel (E7)
    {
        id: 'telemetry-meta-main',
        slot: 'center-main',
        label: 'Meta Telemetry',
        appKey: 'meta',
        routePattern: '/hivecore/studio-v2/app/meta',
        description: 'OS health, Bee utilization, and semantic graph insights',
    },

    // Environment Status Panel (Ops)
    {
        id: 'environment-status-main',
        slot: 'center-main',
        label: 'Environment Status',
        appKey: 'environment-status',
        routePattern: '/hivecore/studio-v2/app/environment-status',
        primary: true,
        description: 'Environment validation, missing vars, and service health diagnostics',
    },

    // System Status Panel (Ops - Phase 1)
    {
        id: 'system-status-main',
        slot: 'center-main',
        label: 'System Status',
        appKey: 'system-status',
        routePattern: '/hivecore/studio-v2/app/system-status',
        primary: true,
        description:
            'Operational truth surface: system health, domain status, and signal freshness',
    },

    // Decision Artifact Panel (Cognitive Architecture V1)
    {
        id: 'decision-artifact-main',
        slot: 'center-main',
        label: 'Decision Artifact',
        appKey: 'decision-artifact',
        routePattern: '/hivecore/studio-v2/app/decision-artifact',
        primary: true,
        description:
            'Decision Artifact consumption surface: view decisionId, path, hash status, and committed timestamp',
    },

    // Ticket Inbox Panel (Autonomous Ticketing V1)
    {
        id: 'ticket-inbox-main',
        slot: 'center-main',
        label: 'Ticket Inbox',
        appKey: 'ticket-inbox',
        routePattern: '/hivecore/studio-v2/app/ticket-inbox',
        primary: true,
        description:
            'Autonomous ticketing consumption surface: view open issues, evidence links, and remediation actions',
    },

    // ============================================
    // RIGHT-RAIL PANELS
    // ============================================

    // Soma Noesis Chat
    {
        id: 'soma-noesis',
        slot: 'right-rail',
        label: 'Soma Noesis',
        isDefault: true,
        description: 'AI assistant chat interface',
    },

    // ============================================
    // DRAWER PANELS
    // ============================================

    // Terminal
    {
        id: 'drawer-terminal',
        slot: 'drawer',
        label: 'Terminal',
        isDefault: true,
        description: 'Command line terminal',
    },

    // Logs
    {
        id: 'drawer-logs',
        slot: 'drawer',
        label: 'Logs',
        description: 'Mission execution logs',
    },

    // Diagnostics
    {
        id: 'drawer-diagnostics',
        slot: 'drawer',
        label: 'Diagnostics',
        description: 'System diagnostics and health',
    },
];

// ============================================
// REGISTRY HELPER FUNCTIONS
// ============================================

/**
 * Get a panel descriptor by ID
 */
export function getPanelById(panelId: string): StudioPanelDescriptor | undefined {
    return studioPanelRegistry.find(p => p.id === panelId);
}

/**
 * Get all panels for a specific slot
 */
export function getPanelsBySlot(slot: StudioPanelSlot): StudioPanelDescriptor[] {
    return studioPanelRegistry.filter(p => p.slot === slot);
}

/**
 * Get the default panel for a slot
 */
export function getDefaultPanelForSlot(slot: StudioPanelSlot): StudioPanelDescriptor | undefined {
    return studioPanelRegistry.find(p => p.slot === slot && p.isDefault);
}

/**
 * Get a panel by its app key
 */
export function getPanelByAppKey(appKey: string): StudioPanelDescriptor | undefined {
    return studioPanelRegistry.find(p => p.appKey === appKey);
}

/**
 * Get all panels that have an app key (for SidebarV2 Apps section)
 */
export function getAppPanels(): StudioPanelDescriptor[] {
    return studioPanelRegistry.filter(p => p.slot === 'center-main' && p.appKey);
}

/**
 * Validate that a panel ID exists in the registry
 */
export function isPanelIdValid(panelId: string): boolean {
    return studioPanelRegistry.some(p => p.id === panelId);
}

/**
 * Validate that a panel can be placed in a given slot
 */
export function canPanelOccupySlot(panelId: string, slot: StudioPanelSlot): boolean {
    const panel = getPanelById(panelId);
    return panel?.slot === slot;
}

// ============================================
// D1.1: DOCK 2.0 HELPER FUNCTIONS
// ============================================

/**
 * Get all primary app panels (always visible in sidebar)
 * D1.1: Primary apps are marked with primary: true
 */
export function getPrimaryAppPanels(): StudioPanelDescriptor[] {
    return studioPanelRegistry.filter(
        p => p.slot === 'center-main' && p.appKey && p.primary === true
    );
}

/**
 * Get all secondary app panels (collapsed into "More" group)
 * D1.1: Secondary apps have appKey but no primary flag or primary: false
 */
export function getSecondaryAppPanels(): StudioPanelDescriptor[] {
    return studioPanelRegistry.filter(p => p.slot === 'center-main' && p.appKey && !p.primary);
}

/**
 * Check if a panel is a primary app
 */
export function isPrimaryApp(panelId: string): boolean {
    const panel = getPanelById(panelId);
    return panel?.primary === true;
}
